import { environment } from '@environment';

export interface AuthState {
  accessToken?: string;
  refreshToken?: string;
}

const LOGIN_STATE_KEY = 'authLoginState';

export function readAuthState(): AuthState | null {
  const raw = localStorage.getItem('authState');
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AuthState;
  } catch {
    return null;
  }
}

export function writeAuthState(s: AuthState): void {
  localStorage.setItem('authState', JSON.stringify(s));
}

export function clearAuth(): void {
  localStorage.removeItem('authState');
}

// A returnUrl is fed to navigateByUrl on return, so it must stay an in-app path and never chain
// into another callback hop — that hop would consume attacker-supplied tokens from its own query.
export function isSafeReturnUrl(url: string | null | undefined): url is string {
  return !!url && url.startsWith('/') && !url.startsWith('//') && !url.startsWith('/\\') && !url.startsWith('/auth/callback');
}

// One-time login state (CSRF binding): the identity app redirects to the callback URL verbatim,
// preserving its query, so the state minted here round-trips and must match this tab's copy.
// sessionStorage can be unavailable (private mode, partitioned or blocked storage) — then there is
// no mint, the login URL carries no state and the callback treats the return as a stateless
// handoff. Login must still work there, so a dead storage never throws out of the redirect path.
export function mintLoginState(): string | null {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  const state = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
  try {
    sessionStorage.setItem(LOGIN_STATE_KEY, state);
  } catch {
    return null;
  }

  return state;
}

export function consumeLoginState(): string | null {
  try {
    const state = sessionStorage.getItem(LOGIN_STATE_KEY);
    sessionStorage.removeItem(LOGIN_STATE_KEY);
    return state;
  } catch {
    return null;
  }
}

export function buildIdentityLoginUrl(returnUrl?: string): string {
  const callback = new URL('/auth/callback', window.location.origin);
  if (isSafeReturnUrl(returnUrl)) {
    callback.searchParams.set('returnUrl', returnUrl);
  }

  const state = mintLoginState();
  if (state) {
    callback.searchParams.set('state', state);
  }

  return `${environment.ID_ENDPOINT}/login?callback=` + encodeURIComponent(callback.toString());
}

export function redirectToIdentity(returnUrl?: string): void {
  window.location.assign(buildIdentityLoginUrl(returnUrl));
}
